import { useState } from 'react';
import { useCreateAction, useAction } from '../../hooks/useActions';

interface ResetPanelProps {
  subscriptionId: string;
}

export default function ResetPanel({ subscriptionId }: ResetPanelProps) {
  const [confirmed, setConfirmed] = useState(false);
  const [activeActionId, setActiveActionId] = useState<string | null>(null);

  const { data: activeAction } = useAction(activeActionId);
  const createAction = useCreateAction();

  const handleReset = async () => {
    if (!confirmed) return;

    try {
      const action = await createAction.mutateAsync({
        subscription_id: subscriptionId,
        action_type: 'reset',
        payload: {},
      });
      setActiveActionId(action.id);
    } catch (error) {
      console.error('Failed to create reset action:', error);
    }
  };

  const handleDismiss = () => {
    setActiveActionId(null);
    setConfirmed(false);
  };

  // Show active action status
  if (activeAction) {
    return (
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        {activeAction.status === 'pending' || activeAction.status === 'claimed' ? (
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600" />
            <div>
              <p className="font-medium">Waiting for server...</p>
              <p className="text-sm text-gray-500">Reset queued</p>
            </div>
          </div>
        ) : activeAction.status === 'running' ? (
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600" />
            <div>
              <p className="font-medium">Resetting controller...</p>
              <p className="text-sm text-gray-500">{activeAction.result || 'In progress'}</p>
            </div>
          </div>
        ) : activeAction.status === 'completed' ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-5 w-5 rounded-full bg-green-500 flex items-center justify-center">
                <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-green-700">Reset Complete</p>
                <p className="text-sm text-gray-500">{activeAction.result}</p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Dismiss
            </button>
          </div>
        ) : activeAction.status === 'failed' ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-5 w-5 rounded-full bg-red-500 flex items-center justify-center">
                <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-red-700">Reset Failed</p>
                <p className="text-sm text-gray-500">{activeAction.result}</p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Dismiss
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div>
      <p className="text-sm text-gray-600 mb-4">
        This will erase your controller configuration and return it to the setup wizard.
        Use this only if you cannot access your controller (e.g., after restoring a backup that failed).
        All UniFi controller configuration will be lost, but this will not impact managed UniFi network devices.
      </p>

      <label className="flex items-start gap-3 mb-4">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
        />
        <span className="text-sm text-gray-700">
          I understand this will erase my controller configuration
        </span>
      </label>

      <button
        onClick={handleReset}
        disabled={!confirmed || createAction.isPending}
        className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {createAction.isPending ? 'Starting...' : 'Reset Controller'}
      </button>

      {createAction.isError && (
        <p className="mt-2 text-sm text-red-600">
          {(() => {
            const error = createAction.error as { response?: { data?: { message?: string } } };
            return error?.response?.data?.message || (createAction.error as Error)?.message || 'Failed to start reset';
          })()}
        </p>
      )}
    </div>
  );
}
